import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styles from './ReportsPage.module.css'
import o from '../components/Overlay.module.css'
import rv from './reports/Overlays.module.css'
import { useAuth } from '../contexts/AuthContext'
import { usePreference } from '../lib/preferences'
import { ApiError, apiJson } from '../lib/api'
import { hasPower, useAccess } from '../lib/access'
import { SkeletonRows, PendingButton, useSlow, inertClass, Failed } from '../components/pending'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { DatePeriod } from './productActivity/DatePeriod'
import { sharedPeriod, useSharedPeriod } from './productActivity/sharedPeriod'
import { datesLabel } from '../lib/keptDates'
import { StillLooking } from '../components/StillLooking'
import { todayLocal } from './timesheets/time'
import { dmy } from './wages/format'
import { workbook, XLSX_TYPE } from './batches/xlsx'
import { fileTask, useTasks } from '../components/TaskCentre'
import { LiveBoard } from './reports/LiveBoard'
import { RealtimeBoard } from './reports/RealtimeBoard'
import { deductionsTask } from './reports/deductionsTask'
import { useChanged, useLayer } from '../components/layers'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { ScheduleDialog } from './reports/ScheduleDialog'
import { ScheduledDelivery } from './reports/ScheduledDelivery'
import { StrikePeriodsSetting } from './reports/StrikePeriodsSetting'
import { scheduledKeys, type Schedule } from './reports/schedules'
import sched from './reports/Schedules.module.css'
import {
  CAT_TONES,
  FILTER_WORDS,
  NO_FILTERS,
  REPORT_ICONS,
  cellText,
  drillHref,
  exportRows,
  isNumeric,
  noteText,
  scopeLabel,
  tableLayout,
  viewConfig,
  viewFilters,
  viewMeta,
  type FilterKey,
  type Filters,
  type Library,
  type ReportDef,
  type ReportResult,
  type SavedView,
} from './reports/model'

// Reports (handoff §7l; CONTROL V4 reportsScreen). A library of fixed
// reports Dataphyll defines, and one shell that renders any of them over the
// library: date period or whole season, the filters the report declares,
// KPIs, a chart, a table whose rows open the operational screen behind them,
// and an .xlsx export of exactly what is shown. Every figure is built by the
// server from the same sources the operational screens read.
//
// The period is Product Activity's, shared with Batches (§7l.3). The whole-
// season switch and the filters carry from one report to the next, as V4's
// rpDate and rpF do. Saved views (§7l.5) are shared by the tenant: open,
// rename, duplicate and delete on home; Save view in the shell, which can
// also update the view the report was opened from.
//
// The Leaderboard and Strike Board open as live dashboards (reports/LiveBoard)
// over the library, and pop out to /live/:key for a wallboard.
//
// Scheduled delivery (§7l.6): Schedule on an open report captures what it
// shows; every schedule is listed on home under Scheduled delivery, and a
// report with an active one says SCHEDULED on its card and Scheduled on its
// header button.
//
// Realtime Collection opens its own board (reports/RealtimeBoard). Not here
// yet, and said so: the yield map layer (see DECISIONS, Reports).

type User = ReturnType<typeof useAuth>['user']
const NO_PINS: string[] = []
type DateMode = 'period' | 'season'

export function ReportsPage() {
  const { user } = useAuth()
  // D1.5: saved views and schedules are the tenant's, shared; only ADMIN and
  // SYSADMIN (shared_reports) create, change or remove them. Everyone with
  // Reports opens them. The server refuses the writes regardless.
  const access = useAccess()
  const shared = hasPower(access, 'shared_reports')
  // D9.3: the Strike Board periods are an administrator's to choose.
  const canSetPeriods = hasPower(access, 'report_settings')
  const [lib, setLib] = useState<Library | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  // Pins are this person's in this workspace, kept on the server (routes/preferences.js).
  const [pins, setPins] = usePreference<string[]>(user, 'reports.pinned', NO_PINS)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [views, setViews] = useState<SavedView[]>([])
  const [viewsError, setViewsError] = useState<string | null>(null)
  const [viewsReady, setViewsReady] = useState(false)
  // The view the open report came from, so Save view can update it.
  const [fromView, setFromView] = useState<SavedView | null>(null)
  const [renaming, setRenaming] = useState<SavedView | null>(null)
  const [dateMode, setDateMode] = useState<DateMode>('period')
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid

  const [libTick, setLibTick] = useState(0)
  useEffect(() => {
    const u = userRef.current
    if (!u) return
    let live = true
    apiJson<Library>(u, '/api/reports')
      .then((r) => live && (setLib(r), setError(null)))
      .catch((e) => live && setError(e?.message || 'Could not load the report library'))
    return () => {
      live = false
    }
  }, [uid, libTick])

  const loadViews = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    try {
      const r = await apiJson<{ views: SavedView[] }>(u, '/api/report-views')
      setViews(r.views)
      setViewsError(null)
    } catch (e: any) {
      setViewsError(e?.message || 'Could not load the saved views')
    } finally {
      setViewsReady(true)
    }
  }, [uid])
  useEffect(() => {
    loadViews()
  }, [loadViews])

  // Every schedule in the workspace, and its operating time zone (null
  // while unset: schedules then send at 9 pm UTC, and the list says so).
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [schedTz, setSchedTz] = useState<string | null>(null)
  const [editing, setEditing] = useState<Schedule | null>(null)
  const loadSchedules = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    try {
      const r = await apiJson<{ schedules: Schedule[]; timezone: string | null }>(u, '/api/report-schedules')
      setSchedules(r.schedules)
      setSchedTz(r.timezone)
    } catch {
      // The library works without them; the list and tags just do not show.
    }
  }, [uid])
  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])
  const scheduleChanged = (s: Schedule) => setSchedules((xs) => (xs.some((x) => x.id === s.id) ? xs.map((x) => (x.id === s.id ? s : x)) : xs.concat(s)))
  // Someone else changed or removed it: say so and show the list as it is.
  const scheduleFailed = (e: any) => {
    setToast(e?.message || 'Could not change the schedule')
    loadSchedules()
  }
  const scheduled = scheduledKeys(schedules)

  // A write to a view someone else has changed or deleted: say so, and show
  // the list as it now is.
  const viewFailed = (e: any) => {
    setToast(e?.message || 'Could not change the saved view')
    if (e?.status === 409 || e?.status === 404) loadViews()
  }

  const openView = (v: SavedView) => {
    setFilters({ ...NO_FILTERS, ...v.filters })
    setDateMode(v.date_mode)
    setFromView(v)
    setOpenKey(v.report_key)
  }
  const openReport = (key: string) => {
    setFromView(null)
    setOpenKey(key)
  }
  const deleteView = async (v: SavedView) => {
    try {
      await apiJson(userRef.current, `/api/report-views/${v.id}`, { method: 'DELETE' })
      setViews((xs) => xs.filter((x) => x.id !== v.id))
      setToast('Saved view deleted')
    } catch (e) {
      viewFailed(e)
    }
  }
  const duplicateView = async (v: SavedView) => {
    try {
      const r = await apiJson<{ view: SavedView }>(userRef.current, `/api/report-views/${v.id}/duplicate`, { method: 'POST', body: {} })
      setViews((xs) => xs.concat(r.view))
      setToast(`Duplicated as ${r.view.name}`)
    } catch (e) {
      viewFailed(e)
    }
  }
  // A view saved or updated from the shell becomes the one the report is
  // open from, so a second save offers to update it rather than copy it.
  const viewSaved = (v: SavedView) => {
    setViews((xs) => (xs.some((x) => x.id === v.id) ? xs.map((x) => (x.id === v.id ? v : x)) : xs.concat(v)))
    setFromView(v)
  }
  const viewRenamed = (v: SavedView) => {
    setViews((xs) => xs.map((x) => (x.id === v.id ? v : x)))
    setRenaming(null)
    setToast('Saved view renamed')
  }
  const reportName = (key: string) => lib?.reports.find((r) => r.key === key)?.name || key

  // Global search links here (h 465-485): ?report= opens a report, ?view= a
  // saved view with its filters, ?q= fills the library's search box. Each is
  // taken once the list it names has loaded, then dropped from the address.
  const [params, setParams] = useSearchParams()
  const linkedReport = params.get('report')
  const linkedView = params.get('view')
  const linkedQ = params.get('q')
  useEffect(() => {
    if (linkedReport === null && linkedView === null && linkedQ === null) return
    if (linkedReport !== null && !lib) return
    if (linkedView !== null && !viewsReady) return
    if (linkedQ !== null) setQuery(linkedQ)
    const v = linkedView ? views.find((x) => x.id === linkedView) : undefined
    if (v) openView(v)
    else if (linkedReport && lib?.reports.some((r) => r.key === linkedReport)) openReport(linkedReport)
    const next = new URLSearchParams(params)
    next.delete('report')
    next.delete('view')
    next.delete('q')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedReport, linkedView, linkedQ, lib, viewsReady])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const togglePin = (key: string) => setPins(pins.includes(key) ? pins.filter((k) => k !== key) : [...pins, key])

  const q = query.trim().toLowerCase()
  const reports = lib?.reports || []
  const match = (r: ReportDef) => !q || (r.name + ' ' + r.blurb).toLowerCase().includes(q)
  const pinned = reports.filter((r) => pins.includes(r.key))
  const open = reports.find((r) => r.key === openKey) || null

  const card = (r: ReportDef) => {
    const cat = lib?.categories.find((c) => c.key === r.cat)
    const tone = CAT_TONES[r.cat] || CAT_TONES.prod
    const dark = !!cat?.dark
    const on = pins.includes(r.key)
    return (
      <div
        key={r.key}
        role="button"
        tabIndex={0}
        className={`${styles.card} ${dark ? styles.cardDark : ''}`}
        style={{ borderColor: tone.line, background: dark ? tone.soft : undefined }}
        onClick={() => openReport(r.key)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openReport(r.key))}
      >
        <div className={styles.cardTop}>
          <span className={styles.cardIcon} style={{ background: dark ? 'rgba(232,185,140,.16)' : tone.soft }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={dark ? tone.tone : tone.ink} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d={REPORT_ICONS[r.key] || REPORT_ICONS.daily} />
            </svg>
          </span>
          <span className={styles.cardName}>{r.name}</span>
          {r.live && <span className={styles.liveBadge}>LIVE</span>}
          {scheduled.has(r.key) && <span className={sched.tag}>SCHEDULED</span>}
          <button
            type="button"
            className={`${styles.star} ${on ? styles.starOn : ''}`}
            title="Pin to the top of Reports"
            aria-label={on ? `Unpin ${r.name}` : `Pin ${r.name}`}
            aria-pressed={on}
            onClick={(e) => {
              e.stopPropagation()
              togglePin(r.key)
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill={on ? '#7a8a5e' : 'none'} stroke={on ? '#7a8a5e' : dark ? '#8a8073' : '#a89e8c'} strokeWidth="2.5" strokeLinejoin="round" aria-hidden>
              <path d="m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9Z" />
            </svg>
          </button>
        </div>
        <span className={styles.cardBlurb}>{r.blurb}</span>
        {!r.built && <span className={styles.notBuilt}>Not deployed yet</span>}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="Reports"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            {canSetPeriods && <StrikePeriodsSetting onSaved={() => setLibTick((n) => n + 1)} />}
            <label className={styles.search}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--dp-muted2)" strokeWidth="2.75" strokeLinecap="round" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reports…" aria-label="Search reports" />
            </label>
          </div>
        }
      />

      <PageBody>
        {error && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}
        {!lib && !error && <p className={styles.muted}>Loading…</p>}

        {lib && (
          <div className={styles.library}>
          {pinned.length > 0 && !q && (
            <section className={styles.section}>
              <div className={styles.eyebrow}>Pinned</div>
              <div className={styles.grid}>{pinned.map(card)}</div>
            </section>
          )}
          {!q && (views.length > 0 || viewsError) && (
            <section className={styles.section} aria-label="Saved views">
              <div className={styles.viewsHead}>
                <span className={styles.eyebrow}>Saved views</span>
                <span className={styles.viewsSub}>Shared with everyone in this subscription</span>
                {schedules.length > 0 && <span className={styles.scheduleCount}>{schedules.length} active schedule{schedules.length === 1 ? '' : 's'}</span>}
              </div>
              {viewsError && <p className={styles.muted}>{viewsError}</p>}
              <div className={styles.viewGrid}>
                {views.map((v) => (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    // Its own name, or it would read as its three icon labels too.
                    aria-label={`Open ${v.name}`}
                    className={styles.viewRow}
                    onClick={() => openView(v)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget && (e.preventDefault(), openView(v))}
                  >
                    <div className={styles.viewText}>
                      <div className={styles.viewName}>{v.name}</div>
                      <div className={styles.viewMeta}>{viewMeta(v, reportName(v.report_key))}</div>
                    </div>
                    {shared && (
                      <>
                        <ViewIcon label={`Rename ${v.name}`} title="Rename this view" d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" onClick={() => setRenaming(v)} />
                        <ViewIcon label={`Duplicate ${v.name}`} title="Duplicate this view" d="M9 9h11v11H9ZM5 15H4V4h11v1" onClick={() => duplicateView(v)} />
                        <ViewIcon label={`Delete ${v.name}`} title="Delete this view" d="M18 6 6 18M6 6l12 12" onClick={() => deleteView(v)} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          {!q && schedules.length > 0 && (
            <ScheduledDelivery
              user={user}
              schedules={schedules}
              timezone={schedTz}
              reports={reports}
              onChanged={scheduleChanged}
              onFailed={scheduleFailed}
              onEdit={setEditing}
              toast={setToast}
              canManage={shared}
            />
          )}
          {lib.categories.map((c) => {
            const cards = reports.filter((r) => r.cat === c.key && match(r))
            if (!cards.length) return null
            const tone = CAT_TONES[c.key] || CAT_TONES.prod
            return (
              <section key={c.key} className={styles.section} aria-label={c.label}>
                <div className={styles.catHead}>
                  <span className={styles.catDot} style={{ background: tone.tone }} />
                  <h2 className={styles.catLabel} style={{ color: c.dark ? 'var(--dp-ink)' : tone.ink }}>
                    {c.label}
                  </h2>
                  <span className={styles.catCount}>
                    {cards.length} report{cards.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className={styles.grid}>{cards.map(card)}</div>
              </section>
            )
          })}
          {q && !reports.some(match) && <div className={styles.noMatch}>No report matches that. Dataphyll can add one. Customers do not build reports here.</div>}
        </div>
        )}
      </PageBody>

      {open && open.built && open.live && !!open.periods?.length &&
        (open.key === 'realtime' ? (
          <RealtimeBoard key={open.key} def={{ key: open.key, name: open.name, periods: open.periods }} wall={false} onClose={() => setOpenKey(null)} />
        ) : (
          <LiveBoard key={open.key} def={{ key: open.key, name: open.name, periods: open.periods }} wall={false} onClose={() => setOpenKey(null)} />
        ))}
      {open && open.built && !open.live && (
        <ReportShell
          key={open.key}
          user={user}
          def={open}
          pinned={pins.includes(open.key)}
          onPin={() => togglePin(open.key)}
          onClose={() => setOpenKey(null)}
          toast={setToast}
          dateMode={dateMode}
          setDateMode={setDateMode}
          f={filters}
          setF={setFilters}
          fromView={fromView && fromView.report_key === open.key ? fromView : null}
          onViewSaved={viewSaved}
          onViewFailed={viewFailed}
          schedules={schedules.filter((s) => s.report_key === open.key)}
          scheduleTimezone={schedTz}
          onScheduled={scheduleChanged}
          onScheduleFailed={scheduleFailed}
        />
      )}
      {editing && (
        <ScheduleDialog
          user={user}
          def={reports.find((r) => r.key === editing.report_key) || { key: editing.report_key, name: editing.report_name, filters: Object.keys(editing.filters) as FilterKey[] }}
          schedule={editing}
          timezone={schedTz}
          onClose={() => setEditing(null)}
          onSaved={(s, message) => {
            setEditing(null)
            scheduleChanged(s)
            setToast(message)
          }}
          onFailed={(e) => {
            setEditing(null)
            scheduleFailed(e)
          }}
        />
      )}
      {open && !open.built && <NotBuiltShell def={open} onClose={() => setOpenKey(null)} />}
      {renaming && <RenameDialog user={user} view={renaming} onClose={() => setRenaming(null)} onRenamed={viewRenamed} onFailed={(e) => (setRenaming(null), viewFailed(e))} />}

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close" title="Close">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden>
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  )
}

function ViewIcon({ label, title, d, onClick }: { label: string; title: string; d: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className={styles.viewIcon}
      aria-label={label}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d={d} />
      </svg>
    </button>
  )
}

// A name already taken stays in the dialog to be changed; a view someone
// else changed or deleted closes it and refreshes the list.
function isConflict(e: any) {
  return e?.body?.code === 'STALE' || e?.status === 404
}

function RenameDialog({ user, view, onClose, onRenamed, onFailed }: { user: User; view: SavedView; onClose: () => void; onRenamed: (v: SavedView) => void; onFailed: (e: unknown) => void }) {
  const [name, setName] = useState(view.name)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once the name
  // has changed. Nothing closes it while its write is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(name)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const go = async () => {
    if (busy) return
    const n = name.trim()
    if (!n) return setErr('Name the view')
    if (n === view.name) return onClose()
    setBusy(true)
    try {
      const r = await apiJson<{ view: SavedView }>(user, `/api/report-views/${view.id}`, { method: 'PUT', body: { version: view.version, name: n } })
      onRenamed(r.view)
    } catch (e: any) {
      if (isConflict(e)) return onFailed(e)
      setErr(e?.message || 'Could not rename the view')
      setBusy(false)
    }
  }
  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${rv.viewModal}`} role="dialog" aria-label="Rename view">
        <div className={o.headText}>
          <h2 className={`${o.title} ${rv.viewTitle}`}>Rename view</h2>
          <p className={`${o.sub} ${rv.viewSub}`}>Everyone in this subscription sees the new name.</p>
        </div>
        <div className={rv.viewBody}>
          <input className={`${o.input} ${rv.viewInput} ${inertClass(busy, slow)}`} value={name} maxLength={120} aria-label="View name" autoFocus onChange={(e) => (setName(e.target.value), setErr(null))} onKeyDown={(e) => e.key === 'Enter' && go()} />
        </div>
        <div className={`${o.foot} ${rv.viewFoot}`}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <Failed text={err} />
          <PendingButton type="button" className={o.primary} onClick={go} busy={busy} pending="Renaming…">
            Rename
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}

interface SaveProps {
  user: User
  def: ReportDef
  config: string
  season: boolean
  body: { date_mode: DateMode; filters: Partial<Filters> }
  fromView: SavedView | null
  onClose: () => void
  onSaved: (v: SavedView, message: string) => void
  onFailed: (e: unknown) => void
}

// V4 "Save this view". Opened from a saved view, it can also update that one.
function SaveViewDialog({ user, def, config, season, body, fromView, onClose, onSaved, onFailed }: SaveProps) {
  const [name, setName] = useState('')
  const [err, setErr] = useState<string | null>(null)
  // Which of the two saves is out, so only the pressed button says so.
  const [busyKind, setBusyKind] = useState<'new' | 'update' | null>(null)
  const busy = busyKind !== null
  const slow = useSlow(busy)
  // A layer over the report (h 425-458): Escape closes this first and leaves
  // the report open, and asks first once a name has been typed. Nothing closes
  // it while a save is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(name)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const saveNew = async () => {
    if (busy) return
    const n = name.trim()
    if (!n) return setErr('Name the view')
    setBusyKind('new')
    try {
      const r = await apiJson<{ view: SavedView }>(user, '/api/report-views', { method: 'POST', body: { report_key: def.key, name: n, ...body } })
      onSaved(r.view, 'Saved for everyone in this subscription')
    } catch (e: any) {
      setErr(e?.message || 'Could not save the view')
      setBusyKind(null)
    }
  }
  const update = async () => {
    if (!fromView || busy) return
    setBusyKind('update')
    try {
      const r = await apiJson<{ view: SavedView }>(user, `/api/report-views/${fromView.id}`, { method: 'PUT', body: { version: fromView.version, ...body } })
      onSaved(r.view, `${r.view.name} updated for everyone in this subscription`)
    } catch (e: any) {
      if (isConflict(e)) return onFailed(e)
      setErr(e?.message || 'Could not update the view')
      setBusyKind(null)
    }
  }
  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${rv.viewModal}`} role="dialog" aria-label="Save this view">
        <div className={o.headText}>
          <h2 className={`${o.title} ${rv.viewTitle}`}>Save this view</h2>
          <p className={`${o.sub} ${rv.viewSub}`}>Saved views are shared with everyone in this subscription, not just you. Give it a name someone else would recognise.</p>
        </div>
        <div className={rv.viewBody}>
          <input className={`${o.input} ${rv.viewInput} ${inertClass(busy, slow)}`} value={name} maxLength={120} aria-label="View name" placeholder="e.g. Maungatapere daily blueberries" autoFocus onChange={(e) => (setName(e.target.value), setErr(null))} onKeyDown={(e) => e.key === 'Enter' && saveNew()} />
          <div className={rv.viewConfig} data-testid="view-config">
            {config}
            {!season && <div className={rv.viewConfigNote}>Opens on the period whoever opens it has chosen, not these dates.</div>}
          </div>
        </div>
        <div className={`${o.foot} ${rv.viewFoot}`}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <Failed text={err} />
          {fromView && (
            <PendingButton type="button" className={`${o.ghost} ${rv.pushRight}`} onClick={update} disabled={busy} busy={busyKind === 'update'} pending="Updating…">
              Update {fromView.name}
            </PendingButton>
          )}
          <PendingButton type="button" className={`${o.primary} ${fromView ? rv.keepLeft : ''}`} onClick={saveNew} disabled={busy} busy={busyKind === 'new'} pending="Saving…">
            {fromView ? 'Save as new view' : 'Save view'}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}

// A live dashboard that is not built yet opens to say so, rather than to a
// wallboard of made-up numbers.
function NotBuiltShell({ def, onClose }: { def: ReportDef; onClose: () => void }) {
  // A layer (h 425-458): Escape and the scrim close it.
  const panelEl = useRef<HTMLDivElement>(null)
  useLayer(true, onClose, { el: panelEl, modal: true })
  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div ref={panelEl} className={`${styles.shell} ${styles.shellDark}`} role="dialog" aria-label={def.name}>
        <div className={styles.shellHead}>
          <h1 className={styles.shellTitle}>{def.name}</h1>
          <div className={styles.shellActions}>
            <CloseButton onClose={onClose} />
          </div>
        </div>
        <div className={styles.shellBody}>
          <p className={styles.blurb}>{def.blurb}</p>
          <div className={styles.notBuiltPanel}>This live dashboard is not deployed yet. It opens here once it is.</div>
        </div>
      </div>
    </>
  )
}

interface ShellProps {
  user: User
  def: ReportDef
  pinned: boolean
  onPin: () => void
  onClose: () => void
  toast: (m: string) => void
  dateMode: DateMode
  setDateMode: (m: DateMode) => void
  f: Filters
  setF: React.Dispatch<React.SetStateAction<Filters>>
  fromView: SavedView | null
  onViewSaved: (v: SavedView) => void
  onViewFailed: (e: unknown) => void
  // This report's schedules, for the header button and "Already scheduled".
  schedules: Schedule[]
  scheduleTimezone: string | null
  onScheduled: (s: Schedule) => void
  onScheduleFailed: (e: unknown) => void
}

function ReportShell({ user, def, pinned, onPin, onClose, toast, dateMode, setDateMode, f, setF, fromView, onViewSaved, onViewFailed, schedules, scheduleTimezone, onScheduled, onScheduleFailed }: ShellProps) {
  const [scheduling, setScheduling] = useState(false)
  const isScheduled = schedules.some((s) => s.enabled)
  const shared = hasPower(useAccess(), 'shared_reports')
  const navigate = useNavigate()
  const { startTask } = useTasks()
  const today = todayLocal()
  // Product Activity's period, shared with Batches (§7l.3).
  const [from, setFrom] = useState(() => sharedPeriod(today, user?.uid).from)
  const [to, setTo] = useState(() => sharedPeriod(today, user?.uid).to)
  const kept = useSharedPeriod(user?.uid, today, from, to)
  // Season by Day is always the whole season; every other report can switch.
  const alwaysSeason = def.dates === 'season'
  const season = alwaysSeason || dateMode === 'season'
  const [data, setData] = useState<ReportResult | null>(null)
  const [shownPath, setShownPath] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const userRef = useRef(user)
  userRef.current = user
  // A layer (h 425-458): Escape and the scrim close the report. It holds only
  // filters, so it never asks. Save view is a layer of its own over it, so
  // Escape closes that first and the report on a second press.
  const panelEl = useRef<HTMLDivElement>(null)
  useLayer(true, onClose, { el: panelEl, modal: true })

  const path = useMemo(() => {
    const p = new URLSearchParams()
    if (season) {
      p.set('season', '1')
      p.set('to', today)
    } else {
      p.set('from', from)
      p.set('to', to)
    }
    for (const k of def.filters) if (f[k]) p.set(k, f[k])
    return `/api/reports/${encodeURIComponent(def.key)}?${p}`
  }, [def.key, def.filters, season, from, to, f, today])

  // A new period or filter redraws the rows (skeleton); a refetch on window
  // focus or reconnect keeps them on screen and moves the "as at" stamp.
  const seq = useRef(0)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    try {
      const r = await apiJson<ReportResult>(u, path)
      if (n !== seq.current) return
      setData(r)
      setError(null)
      setAsAt(new Date())
    } catch (e: any) {
      // A refusal (the report's area, or a group manager's scope) says so,
      // rather than reading as a report with nothing in it (D1.5, D1.7).
      if (n === seq.current) setError(e instanceof ApiError && e.status === 403 ? `Access denied. ${e.message}` : e?.message || 'Could not build this report')
    } finally {
      if (n === seq.current) setShownPath(path)
    }
  }, [path])
  useEffect(() => {
    load()
    return () => {
      seq.current++
    }
  }, [load])
  // h 4167-4171: it fetches again on window focus and on reconnect.
  useRefetch(load)
  const loading = shownPath !== path

  const scope = data ? scopeLabel(data.scope) : season ? 'Season' : scopeLabel({ mode: 'range', from, to, season_start: null })
  const cols = data?.cols || []
  const layout = tableLayout(cols)
  const chartMax = Math.max(1, ...(data?.chart || []).map((b) => b.v || 0))

  const doExport = () => {
    if (!data) return
    const shown = data
    startTask(fileTask(`Export ${def.name}`, scope, `${def.key}.xlsx`, XLSX_TYPE, () => workbook([{ name: def.name, rows: exportRows(shown) }])))
  }

  // The Contractor Report's deductions PDF covers the window the report resolved (a season included) and its contractor.
  const doDeductions = () => {
    if (!data?.scope.from || !data.scope.to) return
    const note = `${scope}${f.contractor ? ` · ${f.contractor}` : ''}`
    startTask(deductionsTask(user, { from: data.scope.from, to: data.scope.to, contractor: f.contractor }, note, dmy(today)))
  }

  const teamName = f.team ? data?.options.teams.find((t) => t.id === f.team)?.name || (fromView?.filters.team === f.team ? fromView.team_name : null) : null

  const filterSelect = (k: FilterKey) => {
    const o = data?.options
    const opts: { v: string; label: string }[] =
      k === 'team' ? (o?.teams || []).map((t) => ({ v: t.id, label: t.name })) : ((k === 'product' ? o?.products : k === 'job' ? o?.jobs : o?.contractors) || []).map((n) => ({ v: n, label: n }))
    const all = FILTER_WORDS[k].all
    // A chosen value stays listed even when the new window has none of it.
    if (f[k] && !opts.some((x) => x.v === f[k])) opts.unshift({ v: f[k], label: k === 'team' ? teamName || 'Chosen team' : f[k] })
    return (
      <select key={k} className={styles.filter} value={f[k]} aria-label={FILTER_WORDS[k].label} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}>
        <option value="">{all}</option>
        {opts.map((x) => (
          <option key={x.v} value={x.v}>
            {x.label}
          </option>
        ))}
      </select>
    )
  }

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div ref={panelEl} className={styles.shell} role="dialog" aria-label={def.name}>
        <StillLooking at={kept.at} onDefaults={season || from === today && to === today} label={datesLabel(from, to, today)} onContinue={kept.confirm} onDefault={() => { setFrom(today); setTo(today) }} />
        <div className={styles.shellHead}>
          <h1 className={styles.shellTitle}>{def.name}</h1>
          <span className={styles.scope} data-testid="report-scope">
            {scope}
          </span>
          {!loading && <AsAt at={asAt} />}
          <div className={styles.shellActions}>
            <button type="button" className={styles.ghostBtn} onClick={onPin} aria-pressed={pinned}>
              {pinned ? 'Pinned' : 'Pin'}
            </button>
            {shared && (
              <>
                <button type="button" className={styles.ghostBtn} onClick={() => setSaving(true)}>
                  Save view
                </button>
                <button type="button" className={`${styles.ghostBtn} ${isScheduled ? sched.scheduledBtn : ''}`} onClick={() => setScheduling(true)}>
                  {isScheduled ? 'Scheduled' : 'Schedule'}
                </button>
              </>
            )}
            <button type="button" className={styles.primaryBtn} onClick={doExport} disabled={!data || loading}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
              Export
            </button>
            <CloseButton onClose={onClose} />
          </div>
        </div>

        <div className={styles.shellBody}>
          <p className={styles.blurb}>{def.blurb}</p>

          <div className={styles.controls}>
            {!season && (
              <DatePeriod
                user={user}
                from={from}
                to={to}
                onApply={(a, b) => {
                  setFrom(a)
                  setTo(b)
                }}
              />
            )}
            {!alwaysSeason && (
              <button type="button" className={`${styles.ghostBtn} ${season ? styles.seasonOn : ''}`} aria-pressed={season} onClick={() => setDateMode(season ? 'period' : 'season')}>
                Whole season
              </button>
            )}
            {season && (
              <span className={styles.seasonNote}>
                {data?.scope.season_start
                  ? `Season runs from ${dmy(data.scope.season_start)}, ${data.scope.season_source === 'dataphyll' ? 'as set by Dataphyll' : 'inferred from the first picking day'}.`
                  : 'The season starts on the first picking day.'}
              </span>
            )}
            {def.filters.map(filterSelect)}
            {def.key === 'contractor' && (
              <button type="button" className={`${styles.ghostBtn} ${styles.controlsEnd}`} onClick={doDeductions} disabled={!data?.scope.from || loading}>
                Quality Control PDF
              </button>
            )}
          </div>

          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}
          {loading && !data && <p className={styles.muted}>Building the report…</p>}

          {data && (
            <div className={loading ? styles.stale : undefined}>
              {data.kpis.length > 0 && (
                <div className={styles.kpis}>
                  {data.kpis.map((k) => (
                    <div key={k.label} className={styles.kpi}>
                      <div className={styles.kpiLabel}>{k.label}</div>
                      <div className={styles.kpiValue}>{cellText(k.value, k.fmt)}</div>
                      {k.note && <div className={styles.kpiNote}>{noteText(k.note)}</div>}
                    </div>
                  ))}
                </div>
              )}

              {data.chart && data.chart.length > 0 && (
                <div className={styles.chartCard}>
                  <div className={styles.eyebrow}>{data.chart_unit}</div>
                  <div className={styles.chart} role="img" aria-label={data.chart_unit}>
                    {data.chart.map((b, i) => (
                      <div key={b.label + i} className={styles.bar}>
                        <span className={styles.barValue}>{b.v ? String(b.v) : ''}</span>
                        <span className={styles.barFill} style={{ height: Math.max(2, Math.round(((b.v || 0) / chartMax) * 100)) + '%', background: b.v ? 'var(--dp-accent)' : 'var(--dp-line)' }} title={b.label} />
                        <span className={styles.barLabel}>{b.date ? dmy(b.date) : b.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.map && <div className={styles.mapPanel}>The map layer is not deployed yet. The table below is the same credited weight by location.</div>}

              {cols.length > 0 && (
                <div className={styles.table} role="table" aria-label={def.name}>
                  <div className={styles.headRow} role="row" style={layout}>
                    {cols.map((c, i) => (
                      <span key={i} role="columnheader" className={isNumeric(c.fmt) ? styles.num : undefined}>
                        {c.label}
                      </span>
                    ))}
                  </div>
                  {loading ? (
                    <SkeletonRows rowClass={styles.row} rowStyle={layout} cols={cols.length} label="Loading report" />
                  ) : data.rows.length === 0 ? (
                    <div className={styles.empty}>Nothing in this period matches those filters.</div>
                  ) : (
                    data.rows.map((r, i) => {
                      const href = r.drill ? drillHref(r.drill, data.scope) : null
                      return (
                        <div
                          key={i}
                          role="row"
                          tabIndex={href ? 0 : undefined}
                          className={`${styles.row} ${href ? styles.rowLink : ''}`}
                          style={layout}
                          onClick={href ? () => navigate(href) : undefined}
                          onKeyDown={href ? (e) => e.key === 'Enter' && navigate(href) : undefined}
                        >
                          {r.cells.map((v, j) => (
                            <span key={j} role="cell" className={`${j === 0 ? styles.firstCell : ''} ${cols[j] && isNumeric(cols[j].fmt) ? styles.num : ''}`}>
                              {cellText(v, cols[j]?.fmt || 'text')}
                            </span>
                          ))}
                        </div>
                      )
                    })
                  )}
                </div>
              )}
              {data.drill && data.rows.some((r) => r.drill) && <div className={styles.drillHint}>{data.drill}: click any row.</div>}

              {data.notes.length > 0 && (
                <ul className={styles.notes}>
                  {data.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      {saving && (
        <SaveViewDialog
          user={user}
          def={def}
          config={viewConfig(def, scope, f, teamName)}
          season={season}
          body={{ date_mode: season ? 'season' : 'period', filters: viewFilters(def, f) }}
          fromView={fromView}
          onClose={() => setSaving(false)}
          onSaved={(v, message) => {
            setSaving(false)
            onViewSaved(v)
            toast(message)
          }}
          onFailed={(e) => {
            setSaving(false)
            onViewFailed(e)
          }}
        />
      )}
      {scheduling && (
        <ScheduleDialog
          user={user}
          def={def}
          body={{ date_mode: season ? 'season' : 'period', filters: viewFilters(def, f) }}
          teamName={teamName}
          existing={schedules}
          timezone={scheduleTimezone}
          onClose={() => setScheduling(false)}
          onSaved={(s, message) => {
            setScheduling(false)
            onScheduled(s)
            toast(message)
          }}
          onFailed={(e) => {
            setScheduling(false)
            onScheduleFailed(e)
          }}
        />
      )}
    </>
  )
}
